<template src="./user-details.html"></template>

<script>
import Vue from 'vue'
import ContextPage from '@/components/context-page/ContextPage'
import Vuelidate from 'vuelidate'
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import { validationMixin } from '@/mixins/validation'
import { hasUserUpdateClaim } from '@/router/auth.js'
import SubsidyUserDetails from '@/components/subsidy/SubsidyUserDetails'
import { EventBus } from '@/router/bus'
import { mask } from 'vue-the-mask'
import { chain, groupBy, map } from 'lodash'// eslint-disable-line no-unused-vars

Vue.use(Vuelidate)

export default {
  name: 'user-details',
  mixins: [validationMixin],
  directives: { mask },
  components: {
    ContextPage,
    SubsidyUserDetails
  },
  data () {
    return {
      pageLoader: false,
      userId: '',
      user: {},
      editMode: false,
      statusEditMode: false,
      UserStatus: {
        PENDING: 1,
        ACTIVE: 2,
        WAITING_ACCOUNT: 3,
        PROCESSED: 4,
        COMPLETED: 5,
        REJECTED: 6,
        ERROR: 7
      },
      status: [
        {
          color: '#CFD8DC',
          dark: false,
          text: this.$t('subsidyUser.subsidyPending')
        },
        {
          color: '#52c0a8',
          dark: true,
          text: this.$t('subsidyUser.subsidyActive')
        },
        {
          color: '#52c0a8',
          dark: true,
          text: this.$t('subsidyUser.subsidyWaitingAccount')
        },
        {
          color: '#52c0a8',
          dark: false,
          text: this.$t('subsidyUser.subsidyProcessed')
        },
        {
          color: '#3d92b6',
          dark: true,
          text: this.$t('subsidyUser.subsidyCompleted')
        },
        {
          color: '#FF5722',
          dark: true,
          text: this.$t('subsidyUser.subsidyRejected')
        },
        {
          color: '#d3454a',
          dark: true,
          text: this.$t('subsidyUser.subsidyError')
        }
      ],
      accountNumberFirstPartyMask: '###',
      accountNumberMiddlePartyMask: '#############',
      accountNumberLastPartyMask: '##',
      registerAccount: {
        uniqueIdentificationNumber: '',
        identificationNumber: '',
        accountNumberFirstParty: '',
        accountNumberMiddleParty: '',
        accountNumberLastParty: '',
        accountNumber: ''
      },
      updateUser: {
        uniqueIdentificationNumber: '',
        rejectionComments: ''
      }
    }
  },
  computed: {
    accountNumberFirstPartyErrors () {
      return this.getValidationErrorsByName('accountNumberFirstParty')
    },
    accountNumberMiddlePartyErrors () {
      return this.getValidationErrorsByName('accountNumberMiddleParty')
    },
    accountNumberLastPartyErrors () {
      return this.getValidationErrorsByName('accountNumberLastParty')
    },
    rejectionCommentsErrors () {
      return this.getStatusValidationErrorsByName('rejectionComments')
    },
    userStatusMessage () {
      return this.formatUserStatus(this.user, this.UserStatus)
    },
    canUpdateUser () {
      return hasUserUpdateClaim()
    }
  },
  created () {
    this.retrieveUser()
    EventBus.$on('page-loader', (loading) => {
      this.pageLoader = loading
    })
  },
  beforeDestroy () {
    EventBus.$off('page-loader')
  },
  methods: {
    retrieveUser () {
      this.pageLoader = true
      this.userId = this.$route.params.userId
      this.$http.get(Vue.$apiConfig.userGet + '/' + this.userId).then(
        response => {
          this.logConsole(response)
          this.user = response.data.payload
          this.pageLoader = false
        },
        error => {
          this.logConsole(error)
          this.formatErrorResponse(error, this.errorCodeSource)
          this.pageLoader = false
        }
      )
    },
    editAccount () {
      this.registerAccount.accountNumberFirstParty = this.user.accountNumber ? this.user.accountNumber.substring(0, 3) : ''
      this.registerAccount.accountNumberMiddleParty = this.user.accountNumber ? parseInt(this.user.accountNumber.substring(3, 16), 10) : ''
      this.registerAccount.accountNumberLastParty = this.user.accountNumber ? this.user.accountNumber.substring(16, 18) : ''
      this.editMode = true
    },
    validateBeforeSubmit () {
      this.$v.registerAccount.$touch()
      if (this.$v.registerAccount.$invalid) {
        this.$nextTick(function () {
          document.body.scrollTop = 0
        })
        return
      }
      this.submitAccount()
    },
    submitAccount () {
      this.pageLoader = true
      this.registerAccount.uniqueIdentificationNumber = this.user.uniqueIdentificationNumber
      this.registerAccount.identificationNumber = this.user.identificationNumber
      this.registerAccount.accountNumber = this.registerAccount.accountNumberFirstParty + this.registerAccount.accountNumberMiddleParty.toString().padStart(13, '0') + this.registerAccount.accountNumberLastParty
      this.$http.post(Vue.$apiConfig.registerAccountPost, [this.registerAccount]).then(
        response => {
          this.pageLoader = false
          this.logConsole(response)
          this.editMode = false
          this.user.accountNumber = this.registerAccount.accountNumber

          EventBus.$emit('alert', {
            variant: 'success',
            message: this.$t('account.registerAccountSuccess')
          })
        },
        error => {
          this.pageLoader = false
          this.logConsole(error)
          this.formatErrorResponse(error)
        })
    },
    validateBeforeUpdateStatus () {
      this.$v.updateUser.$touch()
      if (this.$v.updateUser.$invalid) {
        EventBus.$emit('alert', {
          variant: 'error',
          message: this.$t('errors.someErrorsOccuredCheckListBelow'),
          errors: this.getValidationErrors('updateUser')
        })

        this.$nextTick(function () {
          document.body.scrollTop = 0
        })
        return
      }
      this.submitUpdateStatus()
    },
    submitUpdateStatus () {
      this.pageLoader = true
      this.updateUser.identificationNumber = this.user.identificationNumber
      this.$http.post(Vue.$apiConfig.rejectUserStatusPost, this.updateUser).then(
        response => {
          this.logConsole(response)
          const payload = response.data.payload
          this.statusEditMode = false
          this.user.status = payload.status
          this.user.rejectionDate = payload.rejectionDate
          this.user.rejectionComments = payload.rejectionComments

          EventBus.$emit('alert', {
            variant: 'success',
            message: this.$t('subsidyUser.subsidyUserStatusUpdated')
          })
          this.pageLoader = false
        },
        error => {
          this.pageLoader = false
          this.logConsole(error)
          this.formatErrorResponse(error)
        })
    },
    getValidationErrorsByName (name) {
      if (!this.$v.registerAccount[name].$dirty) return []
      return this.getValidationErrors('registerAccount').filter((x) => { return x.field === name }).map(x => x.msg)
    },
    getStatusValidationErrorsByName (name) {
      if (!this.$v.updateUser[name].$dirty) return []
      return this.getValidationErrors('updateUser').filter((x) => { return x.field === name }).map(x => x.msg)
    }
  },
  validations () {
    const rules = {}
    const updateRules = {}

    Object.assign(rules, {
      accountNumberFirstParty: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(3)
      },
      accountNumberMiddleParty: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(13)
      },
      accountNumberLastParty: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(2)
      }
    })

    Object.assign(updateRules, {
      rejectionComments: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(150)
      }
    })

    return {
      registerAccount: rules,
      updateUser: updateRules
    }
  }
}
</script>

<style scoped>
  .v-card__text {
    font-size: 15px
  }
  .user-info-type {
    font-size: 16px
  }
  .user-info {
    font-size: 16px;
    font-weight: 600
  }
  .custom-loader {
    animation: loader 1s infinite;
    display: flex;
  }

  .role-popout {
    background-color: #E0F2F1 !important;
  }

  @-moz-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @-webkit-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @-o-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .centered-label {
    display: flex;
    justify-content: center;
    flex-direction: column;
  }
</style>
